// Shared post-authentication routine used by callback-style login flows (the magic-link link
// callback and the code-entry confirmation page). Given the credentials returned by an
// authenticate mutation, it populates sessionStorage the same way the badge/SSO flows do and
// returns where to redirect next.
import { fetchPermissions, fetchPartners, type MagicLinkSessionUser } from '@src/apis/reware';
import type { FilteredPermissionResources } from '@src/types';
import { DashboardAccessError } from './authErrors';
import { clearSession } from './authProvider';

const PERMISSIONS_PREFIX = 'config_portal_app_';
const PERMISSION_CODES: FilteredPermissionResources[] = [
  'config_portal_app_plugins',
  'config_portal_app_catalog',
  'config_portal_app_developer_tools',
  'config_portal_app_repair',
  'config_portal_app_looker_dashboards',
];

// The credentials this flow requires, narrowed from the loose codegen `UserPublic` (where every
// token field is nullable) to the invariant we actually depend on: a usable session token.
interface AuthenticatedUser {
  uuid: string;
  sessionToken: string;
  jwt?: string;
  oneTimeToken?: string;
  name?: string;
  email?: string;
}

// Parse the loose mutation result into the narrow shape, enforcing "a session token exists" once,
// here, so the rest of the routine (and any future caller) can't skip the check. The input is the
// same `MagicLinkSessionUser` the authenticate mutations return (a `Pick` of the loose codegen
// `UserPublic`, defined once alongside the mutation).
const toAuthenticatedUser = (user: MagicLinkSessionUser): AuthenticatedUser => {
  const sessionToken = user.token || user.oneTimeToken;
  if (!sessionToken) {
    throw new Error('Authentication failed: missing session token');
  }
  return {
    uuid: user.uuid,
    sessionToken,
    jwt: user.token ?? undefined,
    oneTimeToken: user.oneTimeToken ?? undefined,
    name: user.name ?? undefined,
    email: user.email ?? undefined,
  };
};

/**
 * Fetch and validate everything the session needs (permissions + partners), THEN write the
 * identity fields to sessionStorage and return the post-login redirect path. Returns `/` only when
 * the user has exactly one partner; otherwise `/partners`. Throws — after clearing the
 * session — if the response is missing credentials or the user has no permissions for this
 * application, so a mid-flight failure never leaves a half-authenticated session behind.
 *
 * Clears any prior session up front: the fetches below go through getApiToken, which reads and
 * caches a `badgeToken` in sessionStorage. A leftover badgeToken from an earlier/aborted session
 * would otherwise be reused to fetch THIS user's permissions/partners under the OLD user's token,
 * producing a mixed session. Clearing first guarantees the fetches use this sign-in's credential.
 */
export const completeAuthentication = async (
  user: MagicLinkSessionUser,
): Promise<{ redirectTo: string }> => {
  const authed = toAuthenticatedUser(user);

  clearSession();

  // Do all fallible work (network + validation) before writing the identity fields.
  const allPermissions = await fetchPermissions({ token: authed.sessionToken });
  if (!allPermissions) {
    throw new DashboardAccessError();
  }

  const filteredPermissions = Object.fromEntries(
    Object.entries(allPermissions).filter(([key]) => key.startsWith(PERMISSIONS_PREFIX)),
  );
  if (Object.keys(filteredPermissions).length === 0) {
    // Authenticated fine, but this account has no dashboard permissions — a fresh link won't
    // change that, so signal it distinctly from an auth failure (callers must not loop the user
    // back to request another link).
    throw new DashboardAccessError();
  }

  const partners = await fetchPartners({
    token: authed.sessionToken,
    permissionCodes: PERMISSION_CODES,
  });

  const hasSinglePartner = partners.length === 1;

  // All fetches succeeded — write the identity fields. (getApiToken above may have already cached
  // a fresh `badgeToken`; because we cleared first, that token belongs to this sign-in.)
  sessionStorage.setItem('token', authed.sessionToken);
  sessionStorage.setItem('uuid', authed.uuid);
  // When both a JWT and a one-time token are present, cache the one-time token for the public /
  // ps-configs calls, exactly as the badge flow does.
  if (authed.jwt && authed.oneTimeToken) {
    sessionStorage.setItem('badgeToken', authed.oneTimeToken);
  }
  if (authed.name) sessionStorage.setItem('name', authed.name);
  if (authed.email) sessionStorage.setItem('email', authed.email);
  sessionStorage.setItem('permissions', JSON.stringify(filteredPermissions));
  sessionStorage.setItem('partners', JSON.stringify(partners));
  if (hasSinglePartner) {
    sessionStorage.setItem('selectedPartner', JSON.stringify(partners[0]));
  }

  window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: { uuid: authed.uuid } }));

  return { redirectTo: hasSinglePartner ? '/' : '/partners' };
};
